import { Link, useNavigate, useLocation } from "react-router-dom";
import { useLang } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Radar, LogOut, LayoutDashboard, Menu } from "lucide-react";
import { NotificationsBell } from "./NotificationsBell";
import { useState } from "react";

export const Header = () => {
  const { t, lang, toggle } = useLang();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);

  const navLink = (to, label, testid) => (
    <Link
      to={to}
      data-testid={testid}
      onClick={() => setOpen(false)}
      className={`text-sm font-medium transition-colors hover:text-primary ${
        pathname === to ? "text-primary" : "text-muted-foreground"
      }`}
    >
      {label}
    </Link>
  );

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-background/80 border-b border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between gap-4">
          <Link to="/" data-testid="brand-logo" className="flex items-center gap-2 shrink-0">
            <div className="grid place-items-center h-9 w-9 rounded-xl bg-primary text-primary-foreground">
              <Radar className="h-5 w-5" />
            </div>
            <span className="font-display text-xl font-extrabold tracking-tight">{t("brand")}</span>
          </Link>

          <nav className="hidden md:flex items-center gap-7">
            {navLink("/feed", t("nav_feed"), "nav-feed")}
            {navLink("/pros", t("nav_pros"), "nav-pros")}
            {navLink("/pricing", t("nav_pricing"), "nav-pricing")}
            {navLink("/post", t("nav_post"), "nav-post")}
            {user && navLink("/dashboard", t("nav_dashboard"), "nav-dashboard")}
            {user?.role === "client" && navLink("/my-requests", t("nav_client"), "nav-client")}
          </nav>

          <div className="flex items-center gap-2">
            <button
              data-testid="language-toggle-btn"
              onClick={toggle}
              className="font-mono text-xs font-semibold px-2.5 py-1.5 rounded-full border border-border hover:bg-secondary transition-colors"
            >
              {lang === "fr" ? "FR" : "EN"}
            </button>
            {user ? (
              <div className="flex items-center gap-2">
                <NotificationsBell />
                <Button variant="ghost" size="sm" data-testid="header-dashboard-btn"
                  className="hidden sm:inline-flex" onClick={() => navigate("/dashboard")}>
                  <LayoutDashboard className="h-4 w-4 mr-1.5" /> {user.name?.split(" ")[0]}
                </Button>
                <Button variant="outline" size="sm" data-testid="logout-btn"
                  onClick={() => { logout(); navigate("/"); }}>
                  <LogOut className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <>
                <Button variant="ghost" size="sm" data-testid="header-login-btn"
                  className="hidden sm:inline-flex" onClick={() => navigate("/login")}>
                  {t("login")}
                </Button>
                <Button size="sm" data-testid="header-register-btn" onClick={() => navigate("/register")}>
                  {t("get_started")}
                </Button>
              </>
            )}
            <button className="md:hidden p-2" data-testid="mobile-menu-btn" onClick={() => setOpen(!open)}>
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
        {open && (
          <div className="md:hidden flex flex-col gap-4 pb-4 pt-2">
            {navLink("/feed", t("nav_feed"), "m-nav-feed")}
            {navLink("/pros", t("nav_pros"), "m-nav-pros")}
            {navLink("/pricing", t("nav_pricing"), "m-nav-pricing")}
            {navLink("/post", t("nav_post"), "m-nav-post")}
            {user && navLink("/dashboard", t("nav_dashboard"), "m-nav-dashboard")}
            {user?.role === "client" && navLink("/my-requests", t("nav_client"), "m-nav-client")}
            {!user && navLink("/login", t("login"), "m-nav-login")}
          </div>
        )}
      </div>
    </header>
  );
};
